import { createContext, useContext, ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useWorkspaceLocalStorage } from "@/hooks/useWorkspaceLocalStorage";
import { activeLanguage, formatCurrencyWithSymbol } from "@/lib/i18n/format";

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
}

export const CURRENCIES: CurrencyInfo[] = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { code: "KRW", symbol: "₩", name: "South Korean Won" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "CAD", symbol: "CA$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
  { code: "SEK", symbol: "kr", name: "Swedish Krona" },
  { code: "NOK", symbol: "kr", name: "Norwegian Krone" },
  { code: "DKK", symbol: "kr", name: "Danish Krone" },
  { code: "NZD", symbol: "NZ$", name: "New Zealand Dollar" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "HKD", symbol: "HK$", name: "Hong Kong Dollar" },
  { code: "MXN", symbol: "MX$", name: "Mexican Peso" },
  { code: "BRL", symbol: "R$", name: "Brazilian Real" },
  { code: "ZAR", symbol: "R", name: "South African Rand" },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  { code: "SAR", symbol: "﷼", name: "Saudi Riyal" },
  { code: "PLN", symbol: "zł", name: "Polish Zloty" },
  { code: "THB", symbol: "฿", name: "Thai Baht" },
  { code: "IDR", symbol: "Rp", name: "Indonesian Rupiah" },
  { code: "TRY", symbol: "₺", name: "Turkish Lira" },
  { code: "ILS", symbol: "₪", name: "Israeli Shekel" },
  { code: "PHP", symbol: "₱", name: "Philippine Peso" },
  { code: "MYR", symbol: "RM", name: "Malaysian Ringgit" },
  { code: "CLP", symbol: "CL$", name: "Chilean Peso" },
  { code: "COP", symbol: "COL$", name: "Colombian Peso" },
  { code: "ARS", symbol: "AR$", name: "Argentine Peso" },
  { code: "PEN", symbol: "S/.", name: "Peruvian Sol" },
  { code: "TWD", symbol: "NT$", name: "Taiwan Dollar" },
  { code: "VND", symbol: "₫", name: "Vietnamese Dong" },
  { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
  { code: "EGP", symbol: "E£", name: "Egyptian Pound" },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  { code: "GHS", symbol: "GH₵", name: "Ghanaian Cedi" },
  { code: "CZK", symbol: "Kč", name: "Czech Koruna" },
  { code: "HUF", symbol: "Ft", name: "Hungarian Forint" },
  { code: "RON", symbol: "lei", name: "Romanian Leu" },
  { code: "BGN", symbol: "лв", name: "Bulgarian Lev" },
  { code: "HRK", symbol: "kn", name: "Croatian Kuna" },
  { code: "ISK", symbol: "kr", name: "Icelandic Króna" },
  { code: "RUB", symbol: "₽", name: "Russian Ruble" },
  { code: "UAH", symbol: "₴", name: "Ukrainian Hryvnia" },
  { code: "PKR", symbol: "₨", name: "Pakistani Rupee" },
  { code: "BDT", symbol: "৳", name: "Bangladeshi Taka" },
  { code: "LKR", symbol: "Rs", name: "Sri Lankan Rupee" },
  { code: "QAR", symbol: "QR", name: "Qatari Riyal" },
  { code: "KWD", symbol: "KD", name: "Kuwaiti Dinar" },
  { code: "BHD", symbol: "BD", name: "Bahraini Dinar" },
  { code: "OMR", symbol: "OMR", name: "Omani Rial" },
  { code: "JOD", symbol: "JD", name: "Jordanian Dinar" },
];

interface CurrencyContextType {
  currency: CurrencyInfo;
  setCurrencyCode: (code: string) => void;
  formatAmount: (amount: number) => string;
  symbol: string;
}

/**
 * Money as the grid renders it: whole units, no cents.
 *
 * `Math.round` happens HERE rather than through Intl's rounding so the move to
 * locale-aware formatting kept the exact rounding the grid always had (JS
 * rounds a .5 toward +∞, Intl away from zero — they disagree only on negative
 * halves, and the grid's answer is the one already on screen).
 *
 * Intl then places the symbol where the reader's language puts it — "$1,235"
 * for an English reader, "1.235 $" for a German one — while the SYMBOL itself
 * still comes from this file's table, so English output is unchanged for all 54
 * currencies (see `formatCurrencyWithSymbol`).
 */
function formatWholeUnits(amount: number, language: string, currency: CurrencyInfo): string {
  return formatCurrencyWithSymbol(Math.round(amount), language, currency.code, currency.symbol, {
    maximumFractionDigits: 0,
  });
}

const CurrencyContext = createContext<CurrencyContextType>({
  currency: CURRENCIES[0],
  setCurrencyCode: () => {},
  // Default-context path: no provider mounted, so read the language directly.
  formatAmount: (amount: number) => formatWholeUnits(amount, activeLanguage(), CURRENCIES[0]),
  symbol: "$",
});

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currencyCode, setCurrencyCode] = useWorkspaceLocalStorage<string>("settings:deal-currency", "USD");
  const { i18n } = useTranslation();

  const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];

  // The currency CODE is a workspace setting; the number/symbol layout follows
  // the reader's language. Two settings, two axes.
  //
  // `i18n?.language` rather than `i18n.language`: a dozen component tests mock
  // react-i18next with only `t`, and money must still render there.
  const language = i18n?.language ?? activeLanguage();
  const formatAmount = useCallback((amount: number) => {
    return formatWholeUnits(amount, language, currency);
  }, [currency, language]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrencyCode, formatAmount, symbol: currency.symbol }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
